import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { 
  Plus, Package, Edit3, Trash2, Upload, X, Eye, Lock, Sparkles, 
  ShoppingBag, Search, Tag, Image as ImageIcon, CheckCircle2, 
  AlertTriangle, Grid, List, Layers, ArrowUpRight, TrendingUp,
  DollarSign, ShoppingCart, RefreshCw, BarChart2, ShieldCheck,
  CheckCircle, Clock, Truck, ChevronRight
} from "lucide-react";
import "./VendorProducts.css";

const PRODUCT_BUCKET = "vendor_gallery";

// Helper: Auto-generate a clean e-commerce SKU
const generateSKU = (brandName, title) => {
  const prefix = (brandName || "BZ").replace(/[^a-zA-Z]/g, "").substring(0, 3).toUpperCase();
  const prodCode = (title || "ITEM").replace(/[^a-zA-Z]/g, "").substring(0, 3).toUpperCase();
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${prodCode}-${randomNum}`;
};

export default function VendorProducts() {
  const { vendor } = useOutletContext();
  const [activeSubTab, setActiveSubTab] = useState("overview"); // 'overview' | 'listings' | 'orders'
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Drawer / Form State
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [drawerTab, setDrawerTab] = useState("general");

  // Form Fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [inventoryQty, setInventoryQty] = useState("1");
  const [sku, setSku] = useState("");
  const [category, setCategory] = useState("General");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const canList = Boolean(vendor?.can_list_products);

  useEffect(() => {
    if (vendor?.id && canList) {
      fetchStoreData();
    } else {
      setLoading(false);
    }
  }, [vendor, canList]);

  const fetchStoreData = async () => {
    setLoading(true);
    
    // Fetch Products
    const { data: prods } = await supabase
      .from("products")
      .select("*")
      .eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false });

    // Fetch Orders
    const { data: ords } = await supabase
      .from("vendor_orders")
      .select("*")
      .eq("vendor_id", vendor.id)
      .order("created_at", { ascending: false });

    setProducts(prods || []);
    setOrders(ords || []);
    setLoading(false);
  };

  const openNewDrawer = () => {
    setEditingProduct(null);
    setTitle("");
    setDescription("");
    setPrice("");
    setInventoryQty("1");
    setSku(generateSKU(vendor?.brand_name, "NEW"));
    setCategory("General");
    setImages([]);
    setError("");
    setDrawerTab("general");
    setShowDrawer(true);
  };

  const openEditDrawer = (prod) => {
    setEditingProduct(prod);
    setTitle(prod.title || "");
    setDescription(prod.description || "");
    setPrice(prod.price ? String(prod.price) : "");
    setInventoryQty(prod.inventory_qty !== undefined ? String(prod.inventory_qty) : "1");
    setSku(prod.sku || generateSKU(vendor?.brand_name, prod.title));
    setCategory(prod.category || "General");
    setImages(prod.images || []);
    setError("");
    setDrawerTab("general");
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setEditingProduct(null);
  };

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    if (!editingProduct) {
      setSku(generateSKU(vendor?.brand_name, val));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError("");

    try {
      const uploadedEntries = [];
      for (const file of files) {
        const safeName = file.name.replace(/\s+/g, "_");
        const path = `${vendor.id}/products/${Date.now()}_${safeName}`;
        const { error: upErr } = await supabase.storage.from(PRODUCT_BUCKET).upload(path, file);

        if (upErr) throw upErr;

        const { data: urlData } = supabase.storage.from(PRODUCT_BUCKET).getPublicUrl(path);
        uploadedEntries.push({ path, url: urlData.publicUrl, name: file.name });
      }

      setImages((prev) => [...prev, ...uploadedEntries]);
    } catch (err) {
      setError("Image upload failed: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (indexToRemove) => {
    setImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!title.trim() || !price) {
      setError("Product title and price are required.");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      vendor_id: vendor.id,
      title: title.trim(),
      description: description.trim(),
      price: parseFloat(price),
      inventory_qty: parseInt(inventoryQty, 10) || 0,
      sku: sku.trim() || generateSKU(vendor?.brand_name, title),
      category: category,
      images: images,
      status: parseInt(inventoryQty, 10) > 0 ? "published" : "out_of_stock",
    };

    let res;
    if (editingProduct) {
      res = await supabase
        .from("products")
        .update(payload)
        .eq("id", editingProduct.id)
        .select()
        .single();
    } else {
      res = await supabase
        .from("products")
        .insert([payload])
        .select()
        .single();
    }

    setSaving(false);

    if (res.error) {
      setError("Failed to save product: " + res.error.message);
    } else {
      fetchStoreData();
      closeDrawer();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;

    const { error: err } = await supabase.from("products").delete().eq("id", id);
    if (!err) {
      setProducts((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const { error } = await supabase
      .from("vendor_orders")
      .update({ fulfillment_status: newStatus })
      .eq("id", orderId);

    if (!error) {
      setOrders((prev) => prev.map(o => o.id === orderId ? { ...o, fulfillment_status: newStatus } : o));
    }
  };

  // Metrics Calculations
  const filteredProducts = products.filter((p) => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const totalCatalogValue = products.reduce((acc, curr) => acc + (Number(curr.price || 0) * Number(curr.inventory_qty || 0)), 0);
  const outOfStockItems = products.filter(p => p.inventory_qty <= 0);
  const lowStockItems = products.filter(p => p.inventory_qty > 0 && p.inventory_qty <= 3);
  const totalGrossSales = orders.reduce((acc, curr) => acc + Number(curr.total_amount || 0), 0);
  const pendingOrders = orders.filter(o => o.fulfillment_status === "pending");

  if (!vendor) return <div className="vpr-page-shell"><p>Loading account status…</p></div>;

  /* ── 1. GATED / LOCKED STATE ── */
  if (!canList) {
    return (
      <div className="vpr-page-shell">
        <div className="vpr-top-header">
          <div>
            <h1 className="vpr-main-title">My Shop</h1>
            <p className="vpr-sub-title">List products for sale on your public profile</p>
          </div>
        </div>

        <div className="vpr-locked-card">
          <div className="vpr-locked-badge">
            <Sparkles size={14} /> Feature Preview
          </div>

          <div className="vpr-locked-icon-wrapper">
            <ShoppingBag size={32} />
            <div className="vpr-lock-mini-badge">
              <Lock size={12} />
            </div>
          </div>

          <h2 className="vpr-locked-heading">Product Selling is Coming Soon</h2>
          
          <p className="vpr-locked-body">
            We are currently rolling out direct product listings and instant storefront checkout for{" "}
            <strong>{vendor.brand_name || "vendors"}</strong>. Once activated on your account, visitors at{" "}
            <code>bizbazevents.com/v/{vendor.slug || "your-brand"}</code> will be able to browse and buy directly from your brand.
          </p>

          <div className="vpr-feature-checklist">
            <div className="vpr-feature-row">
              <span className="vpr-feature-bullet" /> Dedicated "Shop" tab on your custom slug
            </div>
            <div className="vpr-feature-row">
              <span className="vpr-feature-bullet" /> Automated SKUs & Inventory Stock Management
            </div>
            <div className="vpr-feature-row">
              <span className="vpr-feature-bullet" /> Order Management & Fulfillment Pipeline
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── 2. FULL E-COMMERCE STORE MANAGEMENT HUB ── */
  return (
    <div className="vpr-page-shell">
      {/* Top Header */}
      <div className="vpr-top-header">
        <div>
          <h1 className="vpr-main-title">Store Operations</h1>
          <p className="vpr-sub-title">
            Live storefront: <strong>bizbazevents.com/v/{vendor.slug}</strong>
          </p>
        </div>
        <div className="vpr-header-actions-row">
          {vendor.slug && (
            <a
              href={`/v/${vendor.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="vpr-live-preview-btn"
            >
              <Eye size={14} /> View Live Shop <ArrowUpRight size={13} />
            </a>
          )}
          <button className="vpr-primary-add-btn" onClick={openNewDrawer}>
            <Plus size={16} /> Add Product
          </button>
        </div>
      </div>

      {/* Sub-Nav Bar (SaaS Style) */}
      <div className="vpr-subnav-bar">
        <button 
          className={`vpr-subnav-item ${activeSubTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveSubTab("overview")}
        >
          <BarChart2 size={15} /> Overview & Analytics
        </button>
        <button 
          className={`vpr-subnav-item ${activeSubTab === "listings" ? "active" : ""}`}
          onClick={() => setActiveSubTab("listings")}
        >
          <Package size={15} /> Listings & Inventory ({products.length})
        </button>
        <button 
          className={`vpr-subnav-item ${activeSubTab === "orders" ? "active" : ""}`}
          onClick={() => setActiveSubTab("orders")}
        >
          <ShoppingCart size={15} /> Orders & Sales
          {pendingOrders.length > 0 && <span className="vpr-badge-count">{pendingOrders.length}</span>}
        </button>
      </div>

      {/* TAB 1: OVERVIEW & ANALYTICS */}
      {activeSubTab === "overview" && (
        <div className="vpr-tab-pane">
          {/* KPI Dashboard */}
          <div className="vpr-metrics-grid">
            <div className="vpr-metric-card">
              <div className="vpr-metric-header">
                <span>Gross Revenue</span>
                <DollarSign size={16} className="vpr-metric-icon" />
              </div>
              <div className="vpr-metric-value">KES {totalGrossSales.toLocaleString()}</div>
              <span className="vpr-metric-trend"><TrendingUp size={12} /> Live tracking</span>
            </div>

            <div className="vpr-metric-card">
              <div className="vpr-metric-header">
                <span>Total Orders</span>
                <ShoppingCart size={16} className="vpr-metric-icon" />
              </div>
              <div className="vpr-metric-value">{orders.length}</div>
              <span className="vpr-metric-sub">{pendingOrders.length} pending fulfillment</span>
            </div>

            <div className="vpr-metric-card">
              <div className="vpr-metric-header">
                <span>Inventory Valuation</span>
                <Tag size={16} className="vpr-metric-icon" />
              </div>
              <div className="vpr-metric-value">KES {totalCatalogValue.toLocaleString()}</div>
              <span className="vpr-metric-sub">{products.length} active listings</span>
            </div>
          </div>

          {/* Stock Health Alerts */}
          <div className="vpr-section-card">
            <div className="vpr-card-header-flex">
              <h3 className="vpr-card-sec-title">Inventory Health & Alerts</h3>
              <span className="vpr-timestamp">Last updated: Just now</span>
            </div>

            {outOfStockItems.length === 0 && lowStockItems.length === 0 ? (
              <div className="vpr-health-healthy">
                <CheckCircle2 size={20} color="#10b981" />
                <span>All product stock levels are healthy! No immediate restocks needed.</span>
              </div>
            ) : (
              <div className="vpr-alerts-list">
                {outOfStockItems.map(p => (
                  <div key={p.id} className="vpr-alert-row vpr-alert-row--danger">
                    <AlertTriangle size={16} />
                    <div className="vpr-alert-info">
                      <strong>{p.title} ({p.sku || "No SKU"})</strong> is out of stock!
                    </div>
                    <button className="vpr-inline-btn" onClick={() => openEditDrawer(p)}>Restock</button>
                  </div>
                ))}
                {lowStockItems.map(p => (
                  <div key={p.id} className="vpr-alert-row vpr-alert-row--warning">
                    <Clock size={16} />
                    <div className="vpr-alert-info">
                      <strong>{p.title}</strong> has low stock ({p.inventory_qty} remaining).
                    </div>
                    <button className="vpr-inline-btn" onClick={() => openEditDrawer(p)}>Update Stock</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: LISTINGS & INVENTORY */}
      {activeSubTab === "listings" && (
        <div className="vpr-tab-pane">
          <div className="vpr-toolbar-card">
            <div className="vpr-search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search by title or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="vpr-clear-search" onClick={() => setSearchQuery("")}>
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="vpr-view-switchers">
              <button
                className={`vpr-switch-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <List size={16} />
              </button>
              <button
                className={`vpr-switch-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <Grid size={16} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="vpr-loading">Loading store items…</div>
          ) : filteredProducts.length === 0 ? (
            <div className="vpr-empty-catalog-card">
              <Package size={36} className="vpr-empty-icon" />
              <h3 className="vpr-empty-title">
                {searchQuery ? "No matching products found" : "Your catalog is empty"}
              </h3>
              <p className="vpr-empty-desc">
                {searchQuery ? "Try checking for typos or searching a different term." : "Create your first listing to start displaying items for sale on your profile."}
              </p>
              {!searchQuery && (
                <button className="vpr-primary-add-btn" onClick={openNewDrawer}>
                  <Plus size={16} /> Create First Listing
                </button>
              )}
            </div>
          ) : viewMode === "table" ? (
            <div className="vpr-table-container">
              <table className="vpr-table">
                <thead>
                  <tr>
                    <th>Item & SKU</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Status</th>
                    <th className="vpr-text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => {
                    const mainImg = p.images?.[0]?.url;
                    return (
                      <tr key={p.id}>
                        <td>
                          <div className="vpr-table-item-cell">
                            <div className="vpr-table-thumb">
                              {mainImg ? (
                                <img src={mainImg} alt={p.title} />
                              ) : (
                                <ImageIcon size={18} className="vpr-no-img-icon" />
                              )}
                            </div>
                            <div className="vpr-table-item-info">
                              <span className="vpr-item-title-text">{p.title}</span>
                              <span className="vpr-item-sku-tag">{p.sku || "AUTO-SKU"}</span>
                            </div>
                          </div>
                        </td>
                        <td className="vpr-price-text">KES {Number(p.price).toLocaleString()}</td>
                        <td>
                          <span className={`vpr-stock-pill ${p.inventory_qty <= 0 ? "vpr-stock-pill--out" : ""}`}>
                            {p.inventory_qty} units
                          </span>
                        </td>
                        <td>
                          <span className={`vpr-status-dot-badge ${p.status === "published" ? "published" : "out"}`}>
                            <span className="vpr-dot-indicator" />
                            {p.status === "published" ? "Active" : "Out of Stock"}
                          </span>
                        </td>
                        <td className="vpr-text-right">
                          <div className="vpr-table-actions">
                            <button className="vpr-icon-btn" onClick={() => openEditDrawer(p)} title="Edit Listing">
                              <Edit3 size={15} />
                            </button>
                            <button className="vpr-icon-btn vpr-icon-btn--danger" onClick={() => handleDelete(p.id)} title="Delete Listing">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="vpr-catalog-grid">
              {filteredProducts.map((product) => {
                const mainImg = product.images?.[0]?.url;
                return (
                  <div key={product.id} className="vpr-item-card">
                    <div className="vpr-item-thumb-box">
                      {mainImg ? (
                        <img src={mainImg} alt={product.title} />
                      ) : (
                        <div className="vpr-no-thumb">No Photo</div>
                      )}
                      <span className={`vpr-stock-tag vpr-stock-tag--${product.status}`}>
                        {product.status === "published" ? "Active" : "Out of Stock"}
                      </span>
                    </div>

                    <div className="vpr-item-details">
                      <span className="vpr-item-sku-tag">{product.sku || "NO-SKU"}</span>
                      <h3 className="vpr-item-name">{product.title}</h3>
                      <p className="vpr-item-price">KES {Number(product.price).toLocaleString()}</p>
                      <div className="vpr-item-stock-count">{product.inventory_qty} in stock</div>
                    </div>

                    <div className="vpr-item-card-actions">
                      <button className="vpr-card-btn" onClick={() => openEditDrawer(product)}>
                        <Edit3 size={14} /> Edit
                      </button>
                      <button className="vpr-card-btn vpr-card-btn--danger" onClick={() => handleDelete(product.id)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: ORDERS & SALES */}
      {activeSubTab === "orders" && (
        <div className="vpr-tab-pane">
          {orders.length === 0 ? (
            <div className="vpr-empty-catalog-card">
              <ShoppingCart size={36} className="vpr-empty-icon" />
              <h3 className="vpr-empty-title">No customer orders yet</h3>
              <p className="vpr-empty-desc">When buyers order products directly from your profile, orders will appear here for fulfillment.</p>
            </div>
          ) : (
            <div className="vpr-orders-list">
              {orders.map((ord) => (
                <div key={ord.id} className="vpr-order-card">
                  <div className="vpr-order-header">
                    <div>
                      <span className="vpr-order-number">#{ord.order_number}</span>
                      <span className="vpr-order-date">{new Date(ord.created_at).toLocaleDateString()}</span>
                    </div>
                    <span className={`vpr-fulfill-badge vpr-fulfill-badge--${ord.fulfillment_status}`}>
                      {ord.fulfillment_status}
                    </span>
                  </div>

                  <div className="vpr-order-buyer">
                    <strong>{ord.customer_name}</strong> • {ord.customer_email} • {ord.customer_phone || "No phone"}
                  </div>

                  <div className="vpr-order-items-summary">
                    {(ord.items || []).map((item, i) => (
                      <div key={i} className="vpr-order-item-row">
                        <span>{item.quantity}x {item.title} ({item.sku || "SKU"})</span>
                        <span>KES {Number(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  <div className="vpr-order-footer">
                    <div className="vpr-order-total">
                      Total: <strong>KES {Number(ord.total_amount).toLocaleString()}</strong>
                    </div>

                    <div className="vpr-order-actions">
                      {ord.fulfillment_status === "pending" && (
                        <button className="vpr-btn-primary" onClick={() => updateOrderStatus(ord.id, "fulfilled")}>
                          <Truck size={14} /> Mark as Fulfilled
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Slide-over Drawer Form */}
      {showDrawer && (
        <div className="vpr-drawer-backdrop" onClick={closeDrawer}>
          <div className="vpr-drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="vpr-drawer-header">
              <div>
                <h2>{editingProduct ? "Edit Product Listing" : "New Product Listing"}</h2>
                <p>Configure product details, SKUs, pricing, and media</p>
              </div>
              <button className="vpr-close-icon-btn" onClick={closeDrawer}>
                <X size={18} />
              </button>
            </div>

            <div className="vpr-drawer-tabs">
              <button
                className={`vpr-drawer-tab ${drawerTab === "general" ? "active" : ""}`}
                onClick={() => setDrawerTab("general")}
              >
                1. Product Info
              </button>
              <button
                className={`vpr-drawer-tab ${drawerTab === "pricing" ? "active" : ""}`}
                onClick={() => setDrawerTab("pricing")}
              >
                2. Price & SKU
              </button>
              <button
                className={`vpr-drawer-tab ${drawerTab === "media" ? "active" : ""}`}
                onClick={() => setDrawerTab("media")}
              >
                3. Gallery ({images.length})
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="vpr-drawer-form">
              {error && <div className="vpr-alert-danger">{error}</div>}

              {drawerTab === "general" && (
                <div className="vpr-tab-content">
                  <div className="vpr-input-group">
                    <label>Product Title *</label>
                    <input
                      type="text"
                      placeholder="e.g. Handcrafted Leather Crossbody Bag"
                      value={title}
                      onChange={handleTitleChange}
                      required
                    />
                  </div>

                  <div className="vpr-input-group">
                    <label>Category</label>
                    <input
                      type="text"
                      placeholder="e.g. Apparel, Accessories, Home Decor"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    />
                  </div>

                  <div className="vpr-input-group">
                    <label>Description</label>
                    <textarea
                      placeholder="Include materials, dimensions, colors, or story behind the piece..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {drawerTab === "pricing" && (
                <div className="vpr-tab-content">
                  <div className="vpr-input-group">
                    <label>Stock Keeping Unit (SKU)</label>
                    <div className="vpr-sku-input-row">
                      <input
                        type="text"
                        value={sku}
                        onChange={(e) => setSku(e.target.value)}
                        placeholder="BZ-PROD-102"
                      />
                      <button 
                        type="button" 
                        className="vpr-btn-secondary"
                        onClick={() => setSku(generateSKU(vendor?.brand_name, title))}
                      >
                        <RefreshCw size={13} /> Auto
                      </button>
                    </div>
                  </div>

                  <div className="vpr-input-two-col">
                    <div className="vpr-input-group">
                      <label>Price (KES) *</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="2500"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        required
                      />
                    </div>

                    <div className="vpr-input-group">
                      <label>Stock Available</label>
                      <input
                        type="number"
                        value={inventoryQty}
                        onChange={(e) => setInventoryQty(e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="vpr-callout-box">
                    <CheckCircle2 size={16} />
                    <span>Listings with stock above 0 are instantly active on your profile.</span>
                  </div>
                </div>
              )}

              {drawerTab === "media" && (
                <div className="vpr-tab-content">
                  <div className="vpr-input-group">
                    <label>Photo Gallery</label>
                    <div className="vpr-upload-area">
                      {images.length > 0 && (
                        <div className="vpr-drawer-gallery-grid">
                          {images.map((img, idx) => (
                            <div key={img.path || idx} className="vpr-gallery-preview-card">
                              <img src={img.url} alt={`Upload ${idx}`} />
                              <button
                                type="button"
                                className="vpr-remove-thumb-btn"
                                onClick={() => removeImage(idx)}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <label className="vpr-dropzone">
                        <Upload size={22} className="vpr-drop-icon" />
                        <span>{uploading ? "Uploading images..." : "Click or drag photos to upload"}</span>
                        <small>Supports PNG, JPG, or WEBP</small>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          hidden
                          onChange={handleImageUpload}
                          disabled={uploading}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="vpr-drawer-footer">
                <button type="button" className="vpr-btn-secondary" onClick={closeDrawer}>
                  Cancel
                </button>
                <button type="submit" className="vpr-btn-primary" disabled={saving || uploading}>
                  {saving ? "Saving…" : editingProduct ? "Save Changes" : "Publish Listing"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}